import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import {
    ariaHideOutside,
    OverlayContainer,
    OverlayProvider,
    useModal,
    useOverlay,
    usePreventScroll,
} from '@react-aria/overlays';
import { mergeProps } from '@react-aria/utils';
import { ComponentThemeWrapper, ThumbnailButton } from '@vds/components';
import { getCookie, hasCookie, setCookie } from 'cookies-next';

import { useRegionalConfiguration } from '@/entities/regional-configuration/hooks/useRegionalConfiguration';
import useRegion from '@/hooks/providers/useRegion';
import { isAppDebug } from '@/utils/appDebug';
import { DEFAULT_MAX_AGE } from '@/utils/constants/common';
import {
    shouldAutoOpenDevDrawerOnDebugError,
    subscribeDebugErrorCaptured,
} from '@/utils/devtools/debug-error-dev-tool';
import { getClientLOB } from '@/utils/server/lob-utils';

import DebugErrorCapture from './DebugErrorCapture';

import styles from './DevDrawer.module.css';

interface DevDrawerProps {
    children?: React.ReactNode;
}

const DEV_DRAWER_DISABLED_COOKIE = 'devDrawerDisabled';
const DEV_DRAWER_TITLE_ID = 'dev-drawer-title';

interface DevDrawerOverlayContentProps {
    onClose: () => void;
    children?: React.ReactNode;
}

const DevDrawerOverlayContent = ({ onClose, children }: DevDrawerOverlayContentProps) => {
    const ref = useRef<HTMLDivElement>(null);

    const { overlayProps, underlayProps } = useOverlay(
        {
            isOpen: true,
            onClose,
            isDismissable: true,
        },
        ref,
    );
    const { modalProps } = useModal();
    const { dialogProps } = useDialog(
        { role: 'dialog', 'aria-labelledby': DEV_DRAWER_TITLE_ID },
        ref,
    );

    usePreventScroll();

    useEffect(() => {
        if (ref.current) {
            return ariaHideOutside([ref.current], { shouldUseInert: true });
        }
        return undefined;
    }, []);

    return (
        <div {...underlayProps} className={styles.overlay}>
            <FocusScope contain restoreFocus autoFocus>
                <div
                    {...mergeProps(overlayProps, modalProps, dialogProps)}
                    ref={ref}
                    className={styles.drawer}
                    aria-modal={true}
                    tabIndex={-1}>
                    <div className={styles.header}>
                        <h1 id={DEV_DRAWER_TITLE_ID} className={styles.title}>
                            Development Tools
                        </h1>
                        <ThumbnailButton
                            className={styles.closeDevTools}
                            label="Close"
                            icon="close"
                            onClick={onClose}></ThumbnailButton>
                    </div>
                    <div className={styles.content}>{children && <>{children}</>}</div>
                </div>
            </FocusScope>
        </div>
    );
};

const DevDrawerOverlay = ({ onClose, children }: DevDrawerOverlayContentProps) => (
    <OverlayProvider>
        <OverlayContainer>
            <DevDrawerOverlayContent onClose={onClose}>{children}</DevDrawerOverlayContent>
        </OverlayContainer>
    </OverlayProvider>
);

// Determine if dev drawer should be shown based on cookie
const shouldShowDevDrawer = (): boolean => {
    if (hasCookie(DEV_DRAWER_DISABLED_COOKIE)) {
        const cookieValue = getCookie(DEV_DRAWER_DISABLED_COOKIE);
        return cookieValue !== 'true';
    }
    return true;
};

// Set or update the cookie to enable/disable dev drawer
const setDevDrawerCookie = (isDisabled: boolean): void => {
    try {
        setCookie(DEV_DRAWER_DISABLED_COOKIE, isDisabled, {
            maxAge: DEFAULT_MAX_AGE,
            path: '/',
        });
    } catch (error) {
        console.warn('Failed to set dev drawer cookie', error);
    }
};

/**
 * Dev tools drawer component w/ cookie-based visibility control
 */
export const DevDrawer = ({ children }: DevDrawerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isDevDrawerEnabled, setIsDevDrawerEnabled] = useState<boolean>(() =>
        shouldShowDevDrawer(),
    );

    const { region } = useRegion();
    const { getConfigByKey } = useRegionalConfiguration({ region });
    const showDevDrawer = getConfigByKey('env.SHOW_DEV_DRAWER') ?? false;
    const lob = getClientLOB();

    // Toggle dev drawer visibility via console or script
    const toggleDevDrawer = (): void => {
        const currentlyEnabled = shouldShowDevDrawer();
        const newEnabledState = !currentlyEnabled;

        setIsDevDrawerEnabled(newEnabledState);
        setDevDrawerCookie(!newEnabledState);

        // Update global override
        if (typeof window !== 'undefined') {
            window.__showDevDrawer = newEnabledState;
        }

        if (!newEnabledState) {
            setIsOpen(false);
        }
    };

    // Force show the dev drawer, ignoring cookie state
    const forceShowDevDrawer = (): void => {
        setIsDevDrawerEnabled(true);
        setDevDrawerCookie(false);
        if (typeof window !== 'undefined') {
            window.__showDevDrawer = true;
        }
    };

    const shouldRenderDevDrawer = showDevDrawer && isDevDrawerEnabled;

    const openDevDrawer = useCallback((): void => {
        if (!shouldRenderDevDrawer) {
            return;
        }

        setIsOpen(true);

        if (typeof window !== 'undefined') {
            window.__openDevDrawerOnError = false;
        }
    }, [shouldRenderDevDrawer]);

    useEffect(() => {
        if (!shouldRenderDevDrawer) {
            setIsOpen(false);
        }
    }, [shouldRenderDevDrawer]);

    // Initialize window controls on mount
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.__toggleDevDrawer = toggleDevDrawer;
            window.__forceShowDevDrawer = forceShowDevDrawer;
            window.__openDevDrawer = openDevDrawer;
            window.__showDevDrawer = isDevDrawerEnabled;

            if (
                window.__openDevDrawerOnError &&
                shouldRenderDevDrawer &&
                shouldAutoOpenDevDrawerOnDebugError()
            ) {
                openDevDrawer();
            }
        }
    }, [isDevDrawerEnabled, openDevDrawer, shouldRenderDevDrawer]);

    useEffect(() => {
        if (!shouldRenderDevDrawer || !shouldAutoOpenDevDrawerOnDebugError()) {
            return undefined;
        }

        if (typeof window !== 'undefined' && window.__openDevDrawerOnError) {
            openDevDrawer();
        }

        return subscribeDebugErrorCaptured(() => {
            openDevDrawer();
        });
    }, [shouldRenderDevDrawer, openDevDrawer]);

    const closeDrawer = useCallback((): void => {
        setIsOpen(false);
    }, []);

    // Toggle drawer open/close
    const toggleDrawer = (): void => {
        setIsOpen((prevIsOpen) => !prevIsOpen);
    };

    // Don't render if non-dev environment or disabled via cookie/script
    if (!shouldRenderDevDrawer) {
        return isAppDebug() ? <DebugErrorCapture /> : null;
    }

    return (
        <>
            {isAppDebug() && <DebugErrorCapture />}
            <ComponentThemeWrapper theme={lob === 'ptv' ? 'pluto-dark' : 'dark'}>
                <ThumbnailButton
                    className={styles.devToggle}
                    onClick={toggleDrawer}
                    icon="settings-outlined"
                    label="Dev Tools"></ThumbnailButton>

                {isOpen && <DevDrawerOverlay onClose={closeDrawer}>{children}</DevDrawerOverlay>}
            </ComponentThemeWrapper>
        </>
    );
};

declare global {
    interface Window {
        __showDevDrawer?: boolean;
        __toggleDevDrawer?: () => void;
        __forceShowDevDrawer?: () => void;
        __openDevDrawer?: () => void;
        __openDevDrawerOnError?: boolean;
    }
}
